import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getCustomers } from "../../../slices/customer";
import {
  CustomersDataTable,
  getCustomerColumns,
} from "../../../components/dashboard/customers/customers-table";
import { ModuleFilter } from "../../../components/utils/ModuleFilter";
import {
  ArrowPathRoundedSquareIcon,
  ArrowTopRightOnSquareIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { toast } from "react-hot-toast";
import {
  mapErrors,
  updateUrlWithException,
  ItemStatus,
  DeliveryAddressTitle,
  dateToString,
} from "../../../components/Utils";
import { syncModule } from "../../../api/company";
import SimplePrompt from "../../../components/utils/simplePrompt";
import CustomerDialog from "../../../components/dashboard/customers/dialog";
import { useNavigate, useSearchParams } from "react-router-dom";
import { debounce } from "lodash";
import { BulkBar } from "../../../components/utils/BulkBar";
import { TableToolbar } from "../../../components/utils/TableToolbar";
import { PMDialog } from "../../../components/utils/PMDialog";
import { useTableView } from "../../../components/utils/PMTableView/useTableView";
import {
  RightDrawer,
  DrawerProps,
  DrawerSection,
} from "../../../components/utils/PMTableView/RightDrawer";

export const CustomerList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const itemState = useSelector((store) => store.customers);
  const { isLoading } = itemState;

  const [searchParams] = useSearchParams();

  // Prevents double-dispatch when handleLoadMore updates controller.page
  const skipNextDispatchRef = useRef(false);

  const getInitialFiltersFromUrl = () => {
    const filtersParam = searchParams.get("filters");
    if (filtersParam) {
      try {
        return JSON.parse(filtersParam);
      } catch (e) {
        console.error("Error parsing filters from URL:", e);
      }
    }
    return [{ name: "active", value: "true" }];
  };

  const deriveView = (filters) => {
    const af = (filters || []).find((f) => f.name === "active");
    if (!af) return "all";
    return String(af.value) === "false" ? "inactive" : "active";
  };

  const [activeView, setActiveView] = useState(() =>
    deriveView(getInitialFiltersFromUrl())
  );
  const [drawerCustomer, setDrawerCustomer] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);

  const [controller, setController] = useState({
    filters: getInitialFiltersFromUrl(),
    page: searchParams.get("page") ? parseInt(searchParams.get("page"), 10) : 1,
    per_page: searchParams.get("per_page")
      ? parseInt(searchParams.get("per_page"), 10)
      : 50,
    query: searchParams.get("query") ? searchParams.get("query") : "",
    sort: searchParams.get("sort") ? searchParams.get("sort") : "asc",
    sortBy: searchParams.get("sortBy")
      ? searchParams.get("sortBy")
      : "customer_number",
    view: searchParams.get("view") ? searchParams.get("view") : "all",
    fields:
      "id,name,customer_number,mobile,email,postal_address,syncStatus,tripletex_sync,active,profile_locked,lock_reason,locked_at,locked_by",
  });

  useEffect(() => {
    updateUrlWithException(
      {
        page: controller.page,
        per_page: controller.per_page,
        query: controller.query,
        sort: controller.sort,
        sortBy: controller.sortBy,
        view: controller.view,
        filters:
          controller.filters && controller.filters.length > 0
            ? JSON.stringify(controller.filters)
            : null,
      },
      "filters"
    );
  }, [controller]);

  // Column definitions + table-view state
  const columns = useMemo(() => getCustomerColumns(t), [t]);
  const columnMeta = useMemo(
    () =>
      columns.map((c) => ({
        key: c.key,
        label: c.label,
        always: c.always,
        defaultVisible: c.defaultVisible,
        groupable: c.groupable,
      })),
    [columns]
  );
  const groupOptions = useMemo(
    () =>
      columns
        .filter((c) => c.groupable)
        .map((c) => ({ value: c.key, label: c.label })),
    [columns]
  );

  const tableView = useTableView({
    tableKey: "customers",
    columns: columnMeta,
    getSnapshot: () => ({
      filters: controller.filters,
      query: controller.query,
      sort: controller.sort,
      sortBy: controller.sortBy,
      view: activeView,
    }),
    applySnapshot: (snap) => {
      setActiveView(snap.view || "all");
      setController((c) => ({
        ...c,
        page: 1,
        filters: snap.filters || [],
        query: snap.query || "",
        sort: snap.sort || c.sort,
        sortBy: snap.sortBy || c.sortBy,
      }));
    },
  });

  const tableSort = { field: controller.sortBy, dir: controller.sort };
  const handleSort = (field) => {
    setController((c) => ({
      ...c,
      page: 1,
      sortBy: field,
      sort: c.sortBy === field && c.sort === "asc" ? "desc" : "asc",
    }));
  };

  const handleFiltersChange = (filters) => {
    const backendFilters = [];
    if (activeView === "active") {
      backendFilters.push({ name: "active", value: "true" });
    } else if (activeView === "inactive") {
      backendFilters.push({ name: "active", value: "false" });
    }
    setController((c) => ({
      ...c,
      page: 1,
      query: filters.query || "",
      filters: backendFilters,
    }));
  };

  const handleQueryChange = (newQuery) => {
    setController((c) => ({
      ...c,
      page: 1,
      query: newQuery,
    }));
  };

  const getInitialFilters = () => ({ query: controller.query });

  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const filterPanelPlaceholders = useMemo(
    () => [
      { key: "email", label: t("customer_list.filter.placeholder_email") },
      { key: "address", label: t("customer_list.filter.placeholder_address") },
      { key: "sync", label: t("customer_list.filter.placeholder_sync") },
      { key: "tags", label: t("customer_list.filter.placeholder_tags") },
      { key: "type", label: t("customer_list.filter.placeholder_type") },
      { key: "region", label: t("customer_list.filter.placeholder_region") },
    ],
    [t]
  );

  // View tabs (active / inactive / all)
  const customerViews = [
    { id: "active", name: t("Active") },
    { id: "inactive", name: t("Inactive") },
    { id: "all", name: t("All") },
  ];

  const handleViewChange = (viewId) => {
    setActiveView(viewId);
    setController((c) => {
      const others = (c.filters || []).filter((f) => f.name !== "active");
      const next = [...others];
      if (viewId === "active") next.push({ name: "active", value: "true" });
      else if (viewId === "inactive")
        next.push({ name: "active", value: "false" });
      return { ...c, page: 1, filters: next };
    });
  };

  // Infinite scroll
  const handleLoadMore = () => {
    const nextPage = (controller.page || 1) + 1;
    skipNextDispatchRef.current = true;
    dispatch(getCustomers({ ...controller, page: nextPage, append: true }));
    setController((c) => ({ ...c, page: nextPage }));
  };

  const debouncedDispatchAction = debounce((ctrl) => {
    dispatch(getCustomers(ctrl));
  }, 250);

  useEffect(() => {
    if (skipNextDispatchRef.current) {
      skipNextDispatchRef.current = false;
      return;
    }
    debouncedDispatchAction(controller);
    return () => {
      debouncedDispatchAction.cancel();
    };
  }, [controller, dispatch]);

  const [syncState, setSyncState] = useState({
    isLoading: false,
    error: false,
    errors: [],
    updated: 0,
    created: 0,
    found: 0,
  });

  const runOrderSync = () => {
    return syncModule("customers")
      .then((res) => {
        setSyncState((prevState) => ({
          ...prevState,
          isLoading: false,
          updated: res?.data?.products_updated,
          created: res?.data?.products_created,
          found: res?.data?.products_found,
        }));
        toast.success(
          `${syncState.updated} was updated out of ${syncState.found} - ${syncState.created} was created`
        );
        return res;
      })
      .catch((err) => {
        setSyncState((prevState) => ({
          ...prevState,
          isLoading: false,
          updated: 0,
          created: 0,
          found: 0,
        }));
        return err;
      });
  };

  const SyncOrders = () => {
    setSyncState({
      isLoading: true,
      error: false,
      errors: [],
      products_updated: 0,
      products_created: 0,
      products_found: 0,
    });

    toast.promise(runOrderSync(), {
      loading: t("Syncing...."),
      success: (res) => {
        if (res.data.errors) throw new Error(mapErrors(res.data.errors));
        if (res.status === 500) throw new Error("Server error");
        return `Sync was successfull`;
      },
      error: t("Something went wrong. Could not save data!"),
    });
  };

  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [syncPromptState, setSyncPromptState] = useState(false);

  const headerActions = [
    {
      component: null,
      label: "Sync API",
      description: "",
      path: null,
      action: () => setSyncPromptState(true),
      icon: ArrowPathRoundedSquareIcon,
      textColor: "text-teal-700",
      bgColor: "bg-white",
      hidden: false,
      menu: true,
    },
  ];

  const listCount = itemState?.count != null ? `${itemState.count}` : null;

  return (
    <>
      <DashboardHeader
        variant="list"
        title={t("Customers")}
        listCount={listCount}
        actions={headerActions}
        mainButton={{
          label: t("New"),
          action: () => setCustomerDialogOpen(true),
          path: null,
          icon: PlusIcon,
        }}
      >
        <div className="p-3 space-y-2 md:p-5 md:space-y-4">
          {/* Mobile: compact search + collapsed filters */}
          <div className="space-y-2 md:hidden">
            <div className="flex items-center gap-2">
              <div className="relative flex h-9 min-w-0 flex-1 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 focus-within:border-slate-400 focus-within:ring-2 focus-within:ring-slate-200">
                <MagnifyingGlassIcon className="h-4 w-4 flex-shrink-0 text-slate-400" />
                <input
                  type="text"
                  value={controller.query}
                  onChange={(e) => handleQueryChange(e.target.value)}
                  placeholder={t("customer_list.search_placeholder")}
                  className="min-w-0 flex-1 border-none bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400"
                />
                {controller.query ? (
                  <button
                    type="button"
                    onClick={() => handleQueryChange("")}
                    className="flex-shrink-0 text-slate-400 hover:text-slate-600"
                    aria-label={t("Clear")}
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => setMobileFiltersOpen(true)}
                className="relative inline-flex h-9 flex-shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 transition-colors hover:border-slate-300 hover:bg-slate-50 focus:outline-none"
                aria-label={t("customer_list.filter_title")}
              >
                <FunnelIcon className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Desktop: full filter card */}
          <div className="hidden md:block">
            <ModuleFilter
              filterCard
              disabled={isLoading}
              onFiltersChange={handleFiltersChange}
              initialFilters={getInitialFilters()}
              searchPlaceholder={t("customer_list.search_placeholder")}
              filterTitle={t("customer_list.filter_title")}
              acceptedStatuses={[]}
            />
          </div>

          {/* Table card */}
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_6px_22px_rgba(0,0,0,0.07)] md:rounded-[18px]">
            <TableToolbar
              views={customerViews}
              activeView={activeView}
              onViewChange={handleViewChange}
              groupOptions={groupOptions}
              activeGroup={tableView.groupBy}
              onGroupChange={tableView.setGroupBy}
              columns={columnMeta}
              columnOrder={tableView.order}
              visibleKeys={tableView.visibleKeys}
              onToggleColumn={tableView.toggleColumn}
              onReorderColumn={tableView.reorderColumn}
              onResetColumns={tableView.resetColumns}
              savedViews={tableView.views}
              activeSavedViewId={tableView.activeViewId}
              savedViewDirty={tableView.dirty}
              onApplySavedView={tableView.applyView}
              onSaveAsView={tableView.saveAsView}
              onUpdateView={tableView.updateView}
              onDeleteView={tableView.deleteView}
              density={tableView.density}
              onDensityChange={tableView.setDensity}
            />
            <CustomersDataTable
              columns={columns}
              visibleKeys={tableView.visibleKeys}
              widths={tableView.widths}
              onResize={tableView.resizeColumn}
              groupBy={tableView.groupBy}
              sort={tableSort}
              onSort={handleSort}
              itemState={itemState}
              onItemsSelect={(items) => setSelectedItems(items)}
              density={tableView.density}
              hasMore={(itemState?.count || 0) > (itemState?.data?.length || 0)}
              onLoadMore={handleLoadMore}
              onRowClick={(customer) => setDrawerCustomer(customer)}
            />
          </div>
        </div>
      </DashboardHeader>

      {/* Floating bulk action bar */}
      <BulkBar
        count={selectedItems.length}
        onClear={() => setSelectedItems([])}
        actions={[
          {
            label: t("Edit"),
            icon: PencilSquareIcon,
            action: () => toast.error("Kommer snart"),
          },
          { separator: true },
          {
            label: t("Delete"),
            icon: TrashIcon,
            danger: true,
            action: () => toast.error("Kommer snart"),
          },
        ]}
      />

      {/* Row detail pull-out drawer */}
      <RightDrawer
        open={!!drawerCustomer}
        onClose={() => setDrawerCustomer(null)}
        eyebrow={
          drawerCustomer
            ? `${t("Number")} ${drawerCustomer.customer_number || ""}`
            : ""
        }
        title={drawerCustomer?.name || drawerCustomer?.displayTitle || t("Customer")}
        actions={[
          {
            label: t("Open"),
            icon: ArrowTopRightOnSquareIcon,
            primary: true,
            onClick: () =>
              navigate(`/dashboard/customers/${drawerCustomer.id}`),
          },
        ]}
      >
        {drawerCustomer && (
          <DrawerSection>
            <DrawerProps
              items={[
                { label: t("Status"), value: <ItemStatus item={drawerCustomer} /> },
                { label: t("Email"), value: drawerCustomer.email || "—" },
                { label: t("Mobile"), value: drawerCustomer.mobile || "—" },
                {
                  label: t("Address"),
                  value:
                    DeliveryAddressTitle({
                      deliveryAddress: drawerCustomer?.postal_address,
                    }) || "—",
                },
                {
                  label: t("Created"),
                  value: drawerCustomer.created_at
                    ? dateToString(drawerCustomer.created_at)
                    : "—",
                },
              ]}
            />
          </DrawerSection>
        )}
      </RightDrawer>

      <SimplePrompt
        title="Sync av ordre"
        description="Er du sikker på at du ønsker å synke ordrer nå?"
        onAccept={() => SyncOrders()}
        open={syncPromptState}
        onClose={() => setSyncPromptState(false)}
      />

      <CustomerDialog
        open={customerDialogOpen}
        onClose={() => setCustomerDialogOpen(false)}
        readOnly={false}
        redirect={true}
      />

      <PMDialog
        open={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        title={t("customer_list.filter_title")}
        size="md"
        dense
        closeOnOutsideClick
      >
        <div className="overflow-y-auto overscroll-contain px-3 pb-3 sm:px-4 sm:pb-4">
          <ModuleFilter
            panelMode
            hideSearch
            disabled={isLoading}
            onFiltersChange={handleFiltersChange}
            initialFilters={getInitialFilters()}
            acceptedStatuses={[]}
            panelPlaceholderSlots={filterPanelPlaceholders}
          />
        </div>
      </PMDialog>
    </>
  );
};
